// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import SyncDisabledRoundedIcon from '@mui/icons-material/SyncDisabledRounded';
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import { usePutEnergyGroupStateMutation } from '@/api/generated/recalc/endpoints/v2/energyGroups.enhanced';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';
import RowActions from '@/components/ui/datagrid/RowActions';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import EnergyXplorerStatusCell from './EnergyXplorerStatusCell';

import { DateTime } from 'luxon';

export default function EnergyXplorerDatagrid(props) {
  const { initialRows = [], isLoading = true } = props;

  const { t } = useTranslation('service-management');

  const localeText = useDatagridLocaleText();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [putEnergyGroupStateMutation] = usePutEnergyGroupStateMutation();

  const handleDesync = async (energyGroupId) => {
    console.log('Desyncing energy group with ID:', energyGroupId);
    if (!energyGroupId) return;
    const result = await putEnergyGroupStateMutation({
      groupId: energyGroupId,
      energyGroupState: 'Desynced',
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Energy group desynced successfully',
          severity: 'success',
        }),
      );
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  function getStatusIcon(status) {
    switch (status) {
      case 'Success':
        return { Color: 'success', StatusIcon: CheckCircleRoundedIcon };
      case 'Warning':
        return { Color: 'warning', StatusIcon: WarningRoundedIcon };
      case 'Error':
        return { Color: 'error', StatusIcon: ErrorRoundedIcon };
      case 'Critical':
        return { Color: 'error', StatusIcon: PriorityHighRoundedIcon };
      default:
        return { Color: 'default', StatusIcon: HelpOutlineRoundedIcon };
    }
  }

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    `EnergyXplorer-Service-DataGrid-Presets`,
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
        },
      ],
      chosenPreset: 0,
    },
  );

  const updateChosenPreset = (patch) =>
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((preset, idx) =>
        idx === prev.chosenPreset ? { ...preset, ...patch } : preset,
      ),
    }));

  const handleVisibilityChange = (model) => {
    updateChosenPreset({ visibilityList: model });
  };

  const handleDensityChange = (newDensity) => {
    updateChosenPreset({ density: newDensity });
  };

  const handleSortModelChange = (newModel) => {
    updateChosenPreset({ sortModel: newModel });
  };

  const handleFilterModelChange = (newModel) => {
    updateChosenPreset({ filterModel: newModel });
  };

  const StatusChip = ({ status }) => {
    const { t } = useTranslation('energyxplorer');
    const label = t(`app_bar.status.${status}`);
    switch (status) {
      case 'Synced':
        return (
          <Tooltip title={label} placement="top">
            <IconButton disableRipple sx={{ cursor: 'default' }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 14,
                      color: 'success.main',
                      bgcolor: 'background.paper',
                      borderRadius: '50%',
                    }}
                  />
                }
              >
                <SyncRoundedIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        );
      case 'Desynced':
        return (
          <Tooltip title={label} placement="top">
            <IconButton disableRipple sx={{ cursor: 'default' }}>
              <CloudSyncRoundedIcon />
            </IconButton>
          </Tooltip>
        );
      case 'Error':
        return (
          <Tooltip title={label} placement="top">
            <IconButton disableRipple sx={{ cursor: 'default' }}>
              <SyncProblemRoundedIcon color="error" />
            </IconButton>
          </Tooltip>
        );
      case 'Processing':
        return (
          <Tooltip title={label} placement="top">
            <IconButton disableRipple sx={{ cursor: 'default' }}>
              <SyncRoundedIcon
                color="info"
                sx={{
                  animation: 'spinReverse 2s linear infinite',
                  '@keyframes spinReverse': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(-360deg)' }, // counter-clockwise
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        );
      default:
        return (
          <Tooltip title={label} placement="top">
            <IconButton disableRipple sx={{ cursor: 'default' }}>
              <SyncDisabledRoundedIcon />
            </IconButton>
          </Tooltip>
        );
    }
  };

  const currentPreset = dataGridState.presets[dataGridState.chosenPreset];

  const columns = [
    { field: 'AccountId', headerName: 'Account ID', flex: 0.8 },
    { field: 'Account', headerName: 'Account' },
    { field: 'name', headerName: 'Name' },
    { field: 'energyGroupId', headerName: 'Energy Group ID', flex: 0.8 },
    { field: 'nodeCount', headerName: 'Nodes', flex: 0.8 },
    { field: 'initiativesCount', headerName: 'Initiatives', flex: 0.8 },
    // { field: 'hasService', headerName: 'Has EnergyXplorer', type: 'boolean' },
    {
      field: 'ExecutionTime',
      headerName: 'Execution Time',
      flex: 1.2,
      renderCell: (params) => {
        if (!params.value) return '-';
        const dt = DateTime.fromISO(params.value);
        if (!dt.isValid) return '-';
        return dt.toLocaleString(DateTime.DATETIME_MED);
      },
    },
    {
      field: 'QoS',
      headerName: 'QoS Score',
      flex: 1,
      type: 'number',
      sortable: true,
      // ✅ grab the score directly from systemQoS
      // valueGetter: (params) => params.row?.systemQoS?.SystemQoSScore ?? null,
      renderCell: (params) => {
        const overall = params?.row?.systemQoS?.OverallStatus;
        const { StatusIcon, Color } = getStatusIcon(overall);

        if (params.value == null) return '-';

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Chip
              variant="outlined"
              icon={
                <StatusIcon
                  sx={{
                    fontSize: 16,
                    color: (theme) =>
                      Color === 'default'
                        ? theme.palette.text.disabled
                        : theme.palette[Color].main,
                  }}
                />
              }
              label={overall}
              color={Color}
              size="small"
              sx={{ mr: 1, width: 100 }}
            />
            <Typography fontWeight="bold">{params.value.toFixed(1)}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'Status',
      headerName: 'Status',
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          height="100%"
          data-no-row-link="true"
        >
          <EnergyXplorerStatusCell
            qos={params.row?.systemQoS}
            row={params.row}
          />
        </Box>
      ),
    },
    {
      field: 'State',
      headerName: 'Synced',
      renderCell: (params) => {
        return <StatusChip status={params.row?.State} />;
      },
    },
    {
      field: 'desync',
      headerName: t('energyxplorer.desync_energy_group'),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            size="small"
            disableElevation
            color="primary"
            onClick={() => {
              handleDesync(params.row.energyGroupId);
            }}
          >
            {t('energyxplorer.desync_energy_group')}
          </Button>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      maxWidth: 100,
      hideable: false,
      filterable: false,
      exportable: false,
      groupable: false,
      renderCell: (params) => {
        return (
          <RowActions
            onView={() => {
              navigate({
                to: `/$accountId/operations/deep-dive/${params.row.energyGroupId}`,
              });
            }}
          />
        );
      },
    },
  ].map((c) => ({ flex: 1, ...c })); // keep your flex default

  console.log('Rendering DataGrid with rows:', initialRows);
  return (
    <DataGridPremium
      showToolbar
      localeText={localeText}
      rows={initialRows}
      columns={columns}
      loading={isLoading}
      defaultGroupingExpansionDepth={-1}
      columnVisibilityModel={currentPreset.visibilityList}
      onColumnVisibilityModelChange={handleVisibilityChange}
      onDensityChange={(density) => handleDensityChange(density)}
      density={currentPreset.density}
      sortModel={currentPreset.sortModel}
      onSortModelChange={handleSortModelChange}
      filterModel={currentPreset.filterModel}
      onFilterModelChange={handleFilterModelChange}
      pagination
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 25 } },
      }}
      pageSizeOptions={[25, 50, 100]}
      slots={{
        toolbar: DataGridToolbar,
      }}
      slotProps={{
        loadingOverlay: {
          variant: 'linear-progress',
          noRowsVariant: 'skeleton',
        },
        toolbar: {
          showColumns: false,
          showDensitySelector: false,
          showExport: false,
          showDivider: false,
        },
      }}
      // onRowClick={(params) => {
      //   setSelectedRow(params.row);
      // }}
    />
  );
}
